import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// ── CONSTANTS ─────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const RED = '#F87171';
const AMBER = '#FBBF24';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';

const fmt = (n, d = 1) => (typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—');
const fmtDate = (d) => d ? new Date(d)?.toLocaleDateString('es-ES') : '—';
const fmtDateTime = (d) => d ? new Date(d)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDateOnly = (d) => d ? new Date(d)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const fmtTimeOnly = (d) => d ? new Date(d)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
  { key: 'temperaturas', label: 'Temperaturas', icon: 'Thermometer' },
  { key: 'limpieza', label: 'Limpieza', icon: 'Sparkles' },
  { key: 'incidencias', label: 'Incidencias', icon: 'AlertTriangle' },
  { key: 'plagas', label: 'Plagas', icon: 'Bug' },
  { key: 'mantenimiento', label: 'Mantenimiento', icon: 'Wrench' },
  { key: 'recepciones', label: 'Recepciones', icon: 'PackageCheck' },
  { key: 'formacion', label: 'Formación', icon: 'GraduationCap' },
  { key: 'informe', label: 'Informe', icon: 'FileText' },
];

// ── STATUS BADGE ──────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = {
    CONFORME: { bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)', text: '#34D399', label: 'CONFORME' },
    PENDIENTE: { bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.3)', text: '#FBBF24', label: 'PENDIENTE' },
    INCIDENCIA: { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', text: '#F87171', label: 'INCIDENCIA' },
    FUERA_RANGO: { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', text: '#F87171', label: 'FUERA RANGO' },
    ABIERTA: { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', text: '#F87171', label: 'ABIERTA' },
    EN_SEGUIMIENTO: { bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.3)', text: '#FBBF24', label: 'EN SEGUIMIENTO' },
    CERRADA: { bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)', text: '#34D399', label: 'CERRADA' },
    REALIZADO: { bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)', text: '#34D399', label: 'REALIZADO' },
    OMITIDO: { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', text: '#F87171', label: 'OMITIDO' },
    MANUAL: { bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)', text: '#60A5FA', label: 'MANUAL' },
    AUTOMATICO: { bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)', text: '#A78BFA', label: 'AUTOMÁTICO' },
  };
  const c = cfg?.[status] || cfg?.PENDIENTE;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold tracking-wide"
      style={{ background: c?.bg, border: `1px solid ${c?.border}`, color: c?.text }}>
      {c?.label}
    </span>
  );
};

// ── CARD WRAPPER ──────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
  <div className={`rounded-xl p-4 ${className}`}
    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
    {children}
  </div>
);

// ── SECTION HEADER ────────────────────────────────────────────
const SectionHeader = ({ title, subtitle = null, action = null }) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <h2 className="text-white font-semibold text-lg">{title}</h2>
      {subtitle && <p className="text-white/50 text-sm mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ── MODAL WRAPPER ─────────────────────────────────────────────
const Modal = ({ open, onClose, title = '', children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{ background: '#161616', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-white font-semibold text-base">{title}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-5 max-h-[80vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// ── INPUT FIELD ───────────────────────────────────────────────
const Field = ({ label, children, required = false }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-white/70 text-sm font-medium">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full rounded-lg px-3 py-2 text-white text-sm outline-none focus:ring-1 focus:ring-[#34D399]/50 transition-all";
const inputStyle = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' };

// ════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ════════════════════════════════════════════════════════════════
const DashboardTab = ({ equipos, temperaturas, incidencias, limpiezaRegistros, limpiezaPrograma, recepciones, plagas, mantenimiento, formacion }) => {
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const getEquipoStatus = (equipo) => {
    const hoy = temperaturas?.filter(t => t?.equipo_id === equipo?.id && t?.fecha_hora?.startsWith(today));
    if (!hoy?.length) return 'PENDIENTE';
    const fuera = hoy?.filter(t => t?.estado === 'FUERA_RANGO');
    if (fuera?.length) return 'INCIDENCIA';
    return 'CONFORME';
  };

  const getEquipoLastReading = (equipo) => {
    const hoy = temperaturas?.filter(t => t?.equipo_id === equipo?.id && t?.fecha_hora?.startsWith(today));
    if (!hoy?.length) return null;
    return hoy?.sort((a, b) => new Date(b?.fecha_hora) - new Date(a?.fecha_hora))?.[0];
  };

  const limpiezaHoy = limpiezaPrograma?.filter(p => p?.proxima_ejecucion <= today);
  const limpiezaStatus = limpiezaHoy?.length === 0 ? 'CONFORME' : 'PENDIENTE';

  const recepcionesHoy = recepciones?.filter(r => r?.fecha?.startsWith(today));
  const recepcionesStatus = recepcionesHoy?.some(r => !r?.conforme) ? 'INCIDENCIA' : recepcionesHoy?.length > 0 ? 'CONFORME' : 'PENDIENTE';

  const incidenciasAbiertas = incidencias?.filter(i => i?.estado === 'ABIERTA' || i?.estado === 'EN_SEGUIMIENTO');
  const incidenciasStatus = incidenciasAbiertas?.length > 0 ? 'INCIDENCIA' : 'CONFORME';

  const plagasVencidas = plagas?.filter(p => p?.proxima_revision && p?.proxima_revision <= today);
  const plagasStatus = plagasVencidas?.length > 0 ? 'INCIDENCIA' : 'CONFORME';

  const mantVencido = mantenimiento?.filter(m => m?.proxima_revision && m?.proxima_revision <= today);
  const mantStatus = mantVencido?.length > 0 ? 'INCIDENCIA' : 'CONFORME';

  const formVencida = formacion?.filter(f => f?.fecha_vencimiento && f?.fecha_vencimiento <= today);
  const formStatus = formVencida?.length > 0 ? 'INCIDENCIA' : 'CONFORME';

  const allStatuses = [
    ...equipos?.map(e => getEquipoStatus(e)),
    limpiezaStatus, recepcionesStatus, incidenciasStatus, plagasStatus, mantStatus, formStatus
  ];
  const globalStatus = allStatuses?.includes('INCIDENCIA') ? 'INCIDENCIAS' : allStatuses?.includes('PENDIENTE') ? 'PENDIENTE' : 'COMPLETO';

  const globalColor = { COMPLETO: ACCENT, PENDIENTE: AMBER, INCIDENCIAS: RED };
  const globalBg = { COMPLETO: 'rgba(52,211,153,0.08)', PENDIENTE: 'rgba(251,191,36,0.08)', INCIDENCIAS: 'rgba(248,113,113,0.08)' };

  const otherItems = [
    { label: 'Limpieza', status: limpiezaStatus, icon: 'Sparkles' },
    { label: 'Recepciones', status: recepcionesStatus, icon: 'PackageCheck' },
    { label: 'Incidencias', status: incidenciasStatus, icon: 'AlertTriangle' },
    { label: 'Control de Plagas', status: plagasStatus, icon: 'Bug' },
    { label: 'Mantenimiento', status: mantStatus, icon: 'Wrench' },
    { label: 'Formación / Documentación', status: formStatus, icon: 'GraduationCap' },
  ];

  return (
    <div className="space-y-5">
      {/* Global indicator */}
      <div className="rounded-2xl p-5 flex items-center gap-5"
        style={{ background: globalBg?.[globalStatus], border: `1px solid ${globalColor?.[globalStatus]}30` }}>
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${globalColor?.[globalStatus]}15`, border: `1px solid ${globalColor?.[globalStatus]}30` }}>
          <Icon name={globalStatus === 'COMPLETO' ? 'ShieldCheck' : globalStatus === 'PENDIENTE' ? 'Clock' : 'ShieldAlert'} size={28} style={{ color: globalColor?.[globalStatus] }} />
        </div>
        <div>
          <p className="text-white/60 text-sm font-medium uppercase tracking-widest mb-1">Autocontrol Hoy</p>
          <p className="text-3xl font-bold" style={{ color: globalColor?.[globalStatus] }}>{globalStatus}</p>
          <p className="text-white/50 text-sm mt-1">{new Date()?.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      {/* Camera cards with real temperature data */}
      {equipos?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {equipos?.map(equipo => {
            const status = getEquipoStatus(equipo);
            const lastReading = getEquipoLastReading(equipo);
            const color = status === 'CONFORME' ? ACCENT : status === 'INCIDENCIA' ? RED : AMBER;
            return (
              <Card key={equipo?.id}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
                      <Icon name="Thermometer" size={15} style={{ color }} />
                    </div>
                    <span className="text-white font-semibold text-sm">{equipo?.nombre}</span>
                  </div>
                  <StatusBadge status={status} />
                </div>
                {lastReading ? (
                  <div className="mt-2">
                    <p className="text-3xl font-bold" style={{ color }}>
                      {fmt(lastReading?.temperatura, 1)} °C
                    </p>
                    <p className="text-white/50 text-sm mt-1">{fmtTimeOnly(lastReading?.fecha_hora)}</p>
                  </div>
                ) : (
                  <p className="text-white/40 text-sm mt-2">Sin lectura hoy — PENDIENTE</p>
                )}
                <div className="mt-3 pt-3 border-t border-white/8">
                  <span className="text-white/40 text-xs">Rango: {equipo?.temperatura_min}–{equipo?.temperatura_max} °C</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Other status grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {otherItems?.map((item, i) => {
          const color = item?.status === 'CONFORME' ? ACCENT : item?.status === 'INCIDENCIA' ? RED : AMBER;
          return (
            <Card key={i}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
                  <Icon name={item?.icon} size={15} style={{ color }} />
                </div>
                <StatusBadge status={item?.status} />
              </div>
              <p className="text-white/80 text-sm font-medium leading-tight">{item?.label}</p>
            </Card>
          );
        })}
      </div>

      {/* Incidencias abiertas */}
      {incidenciasAbiertas?.length > 0 && (
        <Card>
          <SectionHeader title="Incidencias Activas" subtitle={`${incidenciasAbiertas?.length} incidencia(s) requieren atención`} />
          <div className="space-y-2">
            {incidenciasAbiertas?.slice(0, 5)?.map(inc => (
              <div key={inc?.id} className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.15)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-white/90 text-sm font-medium truncate">{inc?.descripcion}</p>
                  <p className="text-white/40 text-xs mt-0.5">{fmtDateTime(inc?.fecha_hora)}</p>
                </div>
                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                  <StatusBadge status={inc?.gravedad} />
                  <StatusBadge status={inc?.estado} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// TEMPERATURAS TAB
// ════════════════════════════════════════════════════════════════
const TemperaturasTab = ({ equipos, temperaturas, onRefresh, userProfile }) => {
  const [showModal, setShowModal] = useState(false);
  const [showIncModal, setShowIncModal] = useState(false);
  const [selectedInc, setSelectedInc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ equipo_id: '', temperatura: '', fecha_hora: new Date()?.toISOString()?.slice(0, 16), observaciones: '' });
  const [incForm, setIncForm] = useState({ causa: '', medida_correctora: '', observaciones: '' });
  const [newLectura, setNewLectura] = useState('');
  const [filterEquipo, setFilterEquipo] = useState('all');
  const [filterDays, setFilterDays] = useState(7);

  const cutoff = filterDays === 0
    ? new Date(new Date()?.toISOString()?.split('T')?.[0] + 'T00:00:00')?.toISOString()
    : new Date(Date.now() - filterDays * 86400000)?.toISOString();

  const filtered = temperaturas?.filter(t =>
    (filterEquipo === 'all' || t?.equipo_id === filterEquipo) &&
    (filterDays === 0
      ? t?.fecha_hora?.startsWith(new Date()?.toISOString()?.split('T')?.[0])
      : t?.fecha_hora >= cutoff)
  ) || [];

  // Daily summary per equipo
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const summaries = equipos?.map(eq => {
    const lecturas = temperaturas?.filter(t => t?.equipo_id === eq?.id && t?.fecha_hora?.startsWith(today)) || [];
    if (!lecturas?.length) return { equipo: eq, lecturas: 0, min: null, max: null, media: null, fuera: 0, estado: 'PENDIENTE' };
    const temps = lecturas?.map(t => t?.temperatura);
    const fuera = lecturas?.filter(t => t?.estado === 'FUERA_RANGO')?.length;
    return {
      equipo: eq,
      lecturas: lecturas?.length,
      min: Math.min(...temps),
      max: Math.max(...temps),
      media: temps?.reduce((a, b) => a + b, 0) / temps?.length,
      fuera,
      estado: fuera > 0 ? 'INCIDENCIA' : 'CONFORME',
    };
  });

  const handleSave = async () => {
    if (!form?.equipo_id || form?.temperatura === '') { setError('Selecciona cámara e introduce temperatura'); return; }
    const temp = parseFloat(form?.temperatura);
    if (isNaN(temp)) { setError('Temperatura no válida'); return; }
    setSaving(true); setError('');
    try {
      const { error: err } = await supabase?.from('appcc_temperaturas')?.insert({
        equipo_id: form?.equipo_id,
        temperatura: temp,
        fecha_hora: new Date(form.fecha_hora)?.toISOString(),
        origen: 'MANUAL',
        usuario_id: userProfile?.id || null,
        observaciones: form?.observaciones || null,
      });
      if (err) throw err;
      setShowModal(false);
      setForm({ equipo_id: '', temperatura: '', fecha_hora: new Date()?.toISOString()?.slice(0, 16), observaciones: '' });
      onRefresh();
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCerrarIncidencia = async () => {
    if (!selectedInc) return;
    setSaving(true); setError('');
    try {
      let lectura_comprobacion_id = null;
      if (newLectura !== '') {
        const temp = parseFloat(newLectura);
        if (!isNaN(temp)) {
          const { data: lec } = await supabase?.from('appcc_temperaturas')?.insert({
            equipo_id: selectedInc?.equipo_id,
            temperatura: temp,
            fecha_hora: new Date()?.toISOString(),
            origen: 'MANUAL',
            usuario_id: userProfile?.id || null,
            observaciones: 'Lectura de comprobación para cierre de incidencia',
          })?.select()?.single();
          lectura_comprobacion_id = lec?.id || null;
        }
      }
      await supabase?.from('appcc_incidencias')?.update({
        causa: incForm?.causa,
        medida_correctora: incForm?.medida_correctora,
        observaciones: incForm?.observaciones,
        estado: 'CERRADA',
        fecha_cierre: new Date()?.toISOString(),
        lectura_comprobacion_id,
        responsable_id: userProfile?.id || null,
      })?.eq('id', selectedInc?.id);
      setShowIncModal(false);
      setSelectedInc(null);
      setIncForm({ causa: '', medida_correctora: '', observaciones: '' });
      setNewLectura('');
      onRefresh();
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Resumen diario */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {summaries?.map(s => (
          <Card key={s?.equipo?.id}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="Thermometer" size={16} style={{ color: ACCENT }} />
                <span className="text-white font-semibold text-sm">{s?.equipo?.nombre}</span>
              </div>
              <StatusBadge status={s?.estado} />
            </div>
            {s?.lecturas > 0 ? (
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[['Mínima', s?.min], ['Máxima', s?.max], ['Media', s?.media]]?.map(([l, v]) => (
                  <div key={l} className="text-center">
                    <p className="text-white/40 text-xs mb-1">{l}</p>
                    <p className="text-white font-bold text-lg">{v !== null ? `${fmt(v)} °C` : '—'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/40 text-sm text-center py-2">Sin lecturas hoy</p>
            )}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/8">
              <span className="text-white/50 text-xs">Lecturas: {s?.lecturas}</span>
              <span className="text-white/50 text-xs">Rango: {s?.equipo?.temperatura_min}–{s?.equipo?.temperatura_max} °C</span>
              {s?.fuera > 0 && <span className="text-red-400 text-xs font-medium">{s?.fuera} fuera de rango</span>}
            </div>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
          <Icon name="Plus" size={15} />
          + Registrar temperatura manual
        </button>
        <select value={filterEquipo} onChange={e => setFilterEquipo(e?.target?.value)}
          className={inputCls} style={{ ...inputStyle, width: 'auto' }}>
          <option value="all">Todas las cámaras</option>
          {equipos?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
        </select>
        <select value={filterDays} onChange={e => setFilterDays(Number(e?.target?.value))}
          className={inputCls} style={{ ...inputStyle, width: 'auto' }}>
          <option value={0}>Hoy</option>
          <option value={7}>7 días</option>
          <option value={30}>30 días</option>
          <option value={365}>Todo</option>
        </select>
      </div>

      {/* Table — FECHA | HORA | EQUIPO | TEMPERATURA | ESTADO | RESPONSABLE | OBSERVACIONES */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {['Fecha', 'Hora', 'Equipo', 'Temperatura', 'Estado', 'Responsable', 'Observaciones']?.map(h => (
                  <th key={h} className="text-left text-white/50 font-medium py-2 px-3 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered?.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-white/30 py-8">Sin lecturas en el período seleccionado</td></tr>
              ) : filtered?.slice(0, 200)?.map(t => (
                <tr key={t?.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="py-2 px-3 text-white/70 whitespace-nowrap">{fmtDateOnly(t?.fecha_hora)}</td>
                  <td className="py-2 px-3 text-white/70 whitespace-nowrap">{fmtTimeOnly(t?.fecha_hora)}</td>
                  <td className="py-2 px-3 text-white/80">{equipos?.find(e => e?.id === t?.equipo_id)?.nombre || '—'}</td>
                  <td className="py-2 px-3 font-bold whitespace-nowrap" style={{ color: t?.estado === 'FUERA_RANGO' ? RED : ACCENT }}>
                    {fmt(t?.temperatura, 1)} °C
                  </td>
                  <td className="py-2 px-3"><StatusBadge status={t?.estado} /></td>
                  <td className="py-2 px-3 text-white/60 text-xs">{t?.user_profiles?.full_name || t?.user_profiles?.email || '—'}</td>
                  <td className="py-2 px-3 text-white/50 text-xs">{t?.observaciones || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered?.length > 200 && (
          <p className="text-white/30 text-xs text-center mt-3">Mostrando 200 de {filtered?.length} registros. Usa el filtro para acotar el período.</p>
        )}
      </Card>

      {/* Manual registration modal */}
      <Modal open={showModal} onClose={() => { setShowModal(false); setError(''); }} title="Registrar Temperatura Manual">
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)' }}>
            <Icon name="Info" size={14} style={{ color: BLUE }} />
            <span className="text-blue-300 text-xs">Este registro quedará identificado como <strong>MANUAL</strong></span>
          </div>
          <Field label="Cámara" required>
            <select value={form?.equipo_id} onChange={e => setForm(f => ({ ...f, equipo_id: e?.target?.value }))}
              className={inputCls} style={inputStyle}>
              <option value="">Seleccionar cámara...</option>
              {equipos?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
            </select>
          </Field>
          <Field label="Temperatura (°C)" required>
            <input type="number" step="0.1" value={form?.temperatura}
              onChange={e => setForm(f => ({ ...f, temperatura: e?.target?.value }))}
              placeholder="Ej: 3.4" className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Fecha y hora" required>
            <input type="datetime-local" value={form?.fecha_hora}
              onChange={e => setForm(f => ({ ...f, fecha_hora: e?.target?.value }))}
              className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Observaciones">
            <textarea value={form?.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e?.target?.value }))}
              rows={2} placeholder="Observaciones opcionales..." className={inputCls} style={inputStyle} />
          </Field>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={() => { setShowModal(false); setError(''); }}
              className="flex-1 py-2 rounded-lg text-white/60 text-sm transition-colors hover:bg-white/5"
              style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// INCIDENCIAS TAB
// ════════════════════════════════════════════════════════════════
const IncidenciasTab = ({ incidencias, equipos, onRefresh, userProfile }) => {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ causa: '', medida_correctora: '', observaciones: '', nueva_lectura: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = incidencias?.filter(i => filter === 'all' || i?.estado === filter) || [];

  const handleUpdate = async () => {
    if (!selected) return;
    setSaving(true); setError('');
    try {
      const updates = {
        causa: form?.causa,
        medida_correctora: form?.medida_correctora,
        observaciones: form?.observaciones,
        responsable_id: userProfile?.id || null,
      };
      if (form?.nueva_lectura !== '') {
        const temp = parseFloat(form?.nueva_lectura);
        if (!isNaN(temp)) {
          const { data: lec } = await supabase?.from('appcc_temperaturas')?.insert({
            equipo_id: selected?.equipo_id,
            temperatura: temp,
            fecha_hora: new Date()?.toISOString(),
            origen: 'MANUAL',
            usuario_id: userProfile?.id || null,
            observaciones: 'Lectura de comprobación',
          })?.select()?.single();
          if (lec) {
            updates.lectura_comprobacion_id = lec?.id;
            updates.estado = 'CERRADA';
            updates.fecha_cierre = new Date()?.toISOString();
          }
        }
      }
      await supabase?.from('appcc_incidencias')?.update(updates)?.eq('id', selected?.id);
      setSelected(null);
      onRefresh();
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        {['all', 'ABIERTA', 'EN_SEGUIMIENTO', 'CERRADA']?.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
            style={{
              background: filter === f ? `${ACCENT}15` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${filter === f ? `${ACCENT}40` : 'rgba(255,255,255,0.1)'}`,
              color: filter === f ? ACCENT : 'rgba(255,255,255,0.6)',
            }}>
            {f === 'all' ? 'Todas' : f?.replace('_', ' ')}
          </button>
        ))}
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {['Fecha', 'Tipo', 'Equipo', 'Descripción', 'Gravedad', 'Estado', 'Acciones']?.map(h => (
                  <th key={h} className="text-left text-white/50 font-medium py-2 px-3 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered?.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-white/30 py-8">Sin incidencias</td></tr>
              ) : filtered?.map(inc => (
                <tr key={inc?.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="py-2 px-3 text-white/60 text-xs">{fmtDateTime(inc?.fecha_hora)}</td>
                  <td className="py-2 px-3 text-white/70">{inc?.tipo}</td>
                  <td className="py-2 px-3 text-white/70">{equipos?.find(e => e?.id === inc?.equipo_id)?.nombre || '—'}</td>
                  <td className="py-2 px-3 text-white/80 max-w-xs truncate">{inc?.descripcion}</td>
                  <td className="py-2 px-3"><StatusBadge status={inc?.gravedad} /></td>
                  <td className="py-2 px-3"><StatusBadge status={inc?.estado} /></td>
                  <td className="py-2 px-3">
                    {inc?.estado !== 'CERRADA' && (
                      <button onClick={() => { setSelected(inc); setForm({ causa: inc?.causa || '', medida_correctora: inc?.medida_correctora || '', observaciones: inc?.observaciones || '', nueva_lectura: '' }); }}
                        className="text-xs px-2 py-1 rounded transition-colors"
                        style={{ background: `${AMBER}15`, border: `1px solid ${AMBER}30`, color: AMBER }}>
                        Gestionar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={!!selected} onClose={() => { setSelected(null); setError(''); }} title="Gestionar Incidencia">
        {selected && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
              <p className="text-red-300 text-sm font-medium">{selected?.descripcion}</p>
              <p className="text-white/50 text-xs mt-1">{fmtDateTime(selected?.fecha_hora)}</p>
            </div>
            <Field label="Causa">
              <textarea value={form?.causa} onChange={e => setForm(f => ({ ...f, causa: e?.target?.value }))}
                rows={2} placeholder="Causa de la incidencia..." className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Medida correctora">
              <textarea value={form?.medida_correctora} onChange={e => setForm(f => ({ ...f, medida_correctora: e?.target?.value }))}
                rows={2} placeholder="Medida correctora aplicada..." className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Nueva lectura de comprobación (°C)">
              <input type="number" step="0.1" value={form?.nueva_lectura}
                onChange={e => setForm(f => ({ ...f, nueva_lectura: e?.target?.value }))}
                placeholder="Ej: 3.8 — si se introduce, la incidencia se cerrará" className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Observaciones">
              <textarea value={form?.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e?.target?.value }))}
                rows={2} className={inputCls} style={inputStyle} />
            </Field>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setSelected(null); setError(''); }}
                className="flex-1 py-2 rounded-lg text-white/60 text-sm hover:bg-white/5 transition-colors"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button>
              <button onClick={handleUpdate} disabled={saving}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// LIMPIEZA TAB
// ════════════════════════════════════════════════════════════════
const LimpiezaTab = ({ programa, registros, onRefresh, userProfile }) => {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const handleRealizado = async (p) => {
    setSaving(p?.id); setError('');
    try {
      await supabase?.from('appcc_limpieza_registros')?.insert({
        programa_id: p?.id,
        fecha_ejecucion: new Date()?.toISOString(),
        estado: 'REALIZADO',
        responsable_id: userProfile?.id || null,
      });
      onRefresh();
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-5">
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <Card>
        <SectionHeader title="Programa de Limpieza" subtitle="Tareas recurrentes de limpieza y desinfección" />
        <div className="space-y-2">
          {programa?.length === 0 && <p className="text-white/30 text-sm text-center py-6">Sin tareas configuradas</p>}
          {programa?.map(p => {
            const vencida = p?.proxima_ejecucion && p?.proxima_ejecucion <= today;
            const realizadaHoy = registros?.some(r => r?.programa_id === p?.id && r?.fecha_ejecucion?.startsWith(today));
            return (
              <div key={p?.id} className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: realizadaHoy ? 'rgba(52,211,153,0.06)' : vencida ? 'rgba(251,191,36,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${realizadaHoy ? 'rgba(52,211,153,0.2)' : vencida ? 'rgba(251,191,36,0.2)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white/90 text-sm font-medium">{p?.zona}</span>
                    <span className="text-white/40 text-xs">·</span>
                    <span className="text-white/60 text-sm">{p?.tarea}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-white/40 text-xs">{p?.frecuencia}</span>
                    {p?.producto_utilizado && <span className="text-white/40 text-xs">· {p?.producto_utilizado}</span>}
                    <span className="text-white/40 text-xs">· Próxima: {fmtDate(p?.proxima_ejecucion)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                  {realizadaHoy ? (
                    <StatusBadge status="REALIZADO" />
                  ) : (
                    <button onClick={() => handleRealizado(p)} disabled={saving === p?.id}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                      style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: saving === p?.id ? 0.6 : 1 }}>
                      {saving === p?.id ? '...' : '✓ Realizado'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <SectionHeader title="Últimos Registros" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {['Fecha', 'Tarea', 'Estado', 'Observaciones']?.map(h => (
                  <th key={h} className="text-left text-white/50 font-medium py-2 px-3 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {registros?.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-white/30 py-6">Sin registros</td></tr>
              ) : registros?.slice(0, 20)?.map(r => (
                <tr key={r?.id} className="border-b border-white/5">
                  <td className="py-2 px-3 text-white/60 text-xs">{fmtDateTime(r?.fecha_ejecucion)}</td>
                  <td className="py-2 px-3 text-white/70">{programa?.find(p => p?.id === r?.programa_id)?.tarea || '—'}</td>
                  <td className="py-2 px-3"><StatusBadge status={r?.estado} /></td>
                  <td className="py-2 px-3 text-white/50 text-xs">{r?.observaciones || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// PLAGAS TAB
// ════════════════════════════════════════════════════════════════
const PlagasTab = ({ plagas, onRefresh, userProfile }) => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ empresa: '', ultima_revision: '', proxima_revision: '', incidencias: '', medidas_correctoras: '' });
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const handleSave = async () => {
    if (!form?.empresa) { setError('Empresa requerida'); return; }
    setSaving(true); setError('');
    try {
      await supabase?.from('appcc_plagas')?.insert({ ...form, ultima_revision: form?.ultima_revision || null, proxima_revision: form?.proxima_revision || null });
      setShowModal(false);
      setForm({ empresa: '', ultima_revision: '', proxima_revision: '', incidencias: '', medidas_correctoras: '' });
      onRefresh();
    } catch (e) { setError(e?.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
          <Icon name="Plus" size={15} /> Nueva revisión
        </button>
      </div>
      <Card>
        <div className="space-y-3">
          {plagas?.length === 0 && <p className="text-white/30 text-sm text-center py-6">Sin registros de control de plagas</p>}
          {plagas?.map(p => {
            const vencida = p?.proxima_revision && p?.proxima_revision <= today;
            return (
              <div key={p?.id} className="p-4 rounded-xl"
                style={{ background: vencida ? 'rgba(248,113,113,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${vencida ? 'rgba(248,113,113,0.2)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold">{p?.empresa}</span>
                  {vencida && <StatusBadge status="INCIDENCIA" />}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-white/40">Última revisión: </span><span className="text-white/70">{fmtDate(p?.ultima_revision)}</span></div>
                  <div><span className="text-white/40">Próxima revisión: </span><span className={vencida ? 'text-red-400 font-medium' : 'text-white/70'}>{fmtDate(p?.proxima_revision)}</span></div>
                </div>
                {p?.incidencias && <p className="text-white/50 text-xs mt-2">Incidencias: {p?.incidencias}</p>}
                {p?.medidas_correctoras && <p className="text-white/50 text-xs mt-1">Medidas: {p?.medidas_correctoras}</p>}
              </div>
            );
          })}
        </div>
      </Card>
      <Modal open={showModal} onClose={() => { setShowModal(false); setError(''); }} title="Nueva Revisión de Plagas">
        <div className="space-y-4">
          <Field label="Empresa" required>
            <input value={form?.empresa} onChange={e => setForm(f => ({ ...f, empresa: e?.target?.value }))} className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Última revisión">
            <input type="date" value={form?.ultima_revision} onChange={e => setForm(f => ({ ...f, ultima_revision: e?.target?.value }))} className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Próxima revisión">
            <input type="date" value={form?.proxima_revision} onChange={e => setForm(f => ({ ...f, proxima_revision: e?.target?.value }))} className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Incidencias">
            <textarea value={form?.incidencias} onChange={e => setForm(f => ({ ...f, incidencias: e?.target?.value }))} rows={2} className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Medidas correctoras">
            <textarea value={form?.medidas_correctoras} onChange={e => setForm(f => ({ ...f, medidas_correctoras: e?.target?.value }))} rows={2} className={inputCls} style={inputStyle} />
          </Field>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={() => { setShowModal(false); setError(''); }} className="flex-1 py-2 rounded-lg text-white/60 text-sm hover:bg-white/5" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MANTENIMIENTO TAB
// ════════════════════════════════════════════════════════════════
const MantenimientoTab = ({ mantenimiento, equipos, onRefresh, userProfile }) => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ equipo_id: '', tipo_equipo: '', descripcion: '', frecuencia: 'MENSUAL', ultima_revision: '', proxima_revision: '', empresa_mantenimiento: '', observaciones: '' });
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const handleSave = async () => {
    if (!form?.tipo_equipo || !form?.descripcion) { setError('Tipo de equipo y descripción requeridos'); return; }
    setSaving(true); setError('');
    try {
      await supabase?.from('appcc_mantenimiento')?.insert({
        ...form,
        equipo_id: form?.equipo_id || null,
        ultima_revision: form?.ultima_revision || null,
        proxima_revision: form?.proxima_revision || null,
        responsable_id: userProfile?.id || null,
      });
      setShowModal(false);
      setForm({ equipo_id: '', tipo_equipo: '', descripcion: '', frecuencia: 'MENSUAL', ultima_revision: '', proxima_revision: '', empresa_mantenimiento: '', observaciones: '' });
      onRefresh();
    } catch (e) { setError(e?.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
          <Icon name="Plus" size={15} /> Nuevo mantenimiento
        </button>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {['Equipo/Tipo', 'Descripción', 'Frecuencia', 'Última revisión', 'Próxima revisión', 'Estado']?.map(h => (
                  <th key={h} className="text-left text-white/50 font-medium py-2 px-3 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mantenimiento?.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-white/30 py-8">Sin registros de mantenimiento</td></tr>
              ) : mantenimiento?.map(m => {
                const vencido = m?.proxima_revision && m?.proxima_revision <= today;
                return (
                  <tr key={m?.id} className="border-b border-white/5 hover:bg-white/3">
                    <td className="py-2 px-3 text-white/80">{equipos?.find(e => e?.id === m?.equipo_id)?.nombre || m?.tipo_equipo}</td>
                    <td className="py-2 px-3 text-white/70 max-w-xs truncate">{m?.descripcion}</td>
                    <td className="py-2 px-3 text-white/60">{m?.frecuencia || '—'}</td>
                    <td className="py-2 px-3 text-white/60">{fmtDate(m?.ultima_revision)}</td>
                    <td className="py-2 px-3" style={{ color: vencido ? RED : 'rgba(255,255,255,0.6)' }}>{fmtDate(m?.proxima_revision)}</td>
                    <td className="py-2 px-3"><StatusBadge status={vencido ? 'INCIDENCIA' : 'CONFORME'} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <Modal open={showModal} onClose={() => { setShowModal(false); setError(''); }} title="Nuevo Mantenimiento">
        <div className="space-y-4">
          <Field label="Equipo (opcional)">
            <select value={form?.equipo_id} onChange={e => setForm(f => ({ ...f, equipo_id: e?.target?.value }))} className={inputCls} style={inputStyle}>
              <option value="">Sin equipo específico</option>
              {equipos?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
            </select>
          </Field>
          <Field label="Tipo de equipo" required>
            <input value={form?.tipo_equipo} onChange={e => setForm(f => ({ ...f, tipo_equipo: e?.target?.value }))} placeholder="Ej: Cámara frigorífica, Vitrina..." className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Descripción" required>
            <textarea value={form?.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e?.target?.value }))} rows={2} className={inputCls} style={inputStyle} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Última revisión">
              <input type="date" value={form?.ultima_revision} onChange={e => setForm(f => ({ ...f, ultima_revision: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Próxima revisión">
              <input type="date" value={form?.proxima_revision} onChange={e => setForm(f => ({ ...f, proxima_revision: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
          </div>
          <Field label="Empresa mantenimiento">
            <input value={form?.empresa_mantenimiento} onChange={e => setForm(f => ({ ...f, empresa_mantenimiento: e?.target?.value }))} className={inputCls} style={inputStyle} />
          </Field>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={() => { setShowModal(false); setError(''); }} className="flex-1 py-2 rounded-lg text-white/60 text-sm hover:bg-white/5" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// RECEPCIONES TAB
// ════════════════════════════════════════════════════════════════
const RecepcionesTab = ({ recepciones, onRefresh, userProfile }) => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ proveedor_nombre: '', producto: '', lote: '', temperatura: '', estado_embalaje: 'Correcto', fecha_caducidad: '', conforme: true, observaciones: '' });

  const handleSave = async () => {
    if (!form?.proveedor_nombre || !form?.producto) { setError('Proveedor y producto requeridos'); return; }
    setSaving(true); setError('');
    try {
      await supabase?.from('appcc_recepciones')?.insert({
        ...form,
        temperatura: form?.temperatura !== '' ? parseFloat(form?.temperatura) : null,
        fecha_caducidad: form?.fecha_caducidad || null,
        responsable_id: userProfile?.id || null,
      });
      setShowModal(false);
      setForm({ proveedor_nombre: '', producto: '', lote: '', temperatura: '', estado_embalaje: 'Correcto', fecha_caducidad: '', conforme: true, observaciones: '' });
      onRefresh();
    } catch (e) { setError(e?.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
          <Icon name="Plus" size={15} /> Nueva recepción
        </button>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {['Fecha', 'Proveedor', 'Producto', 'Lote', 'Temp.', 'Embalaje', 'Caducidad', 'Estado']?.map(h => (
                  <th key={h} className="text-left text-white/50 font-medium py-2 px-3 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recepciones?.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-white/30 py-8">Sin recepciones registradas</td></tr>
              ) : recepciones?.map(r => (
                <tr key={r?.id} className="border-b border-white/5 hover:bg-white/3">
                  <td className="py-2 px-3 text-white/60 text-xs">{fmtDateTime(r?.fecha)}</td>
                  <td className="py-2 px-3 text-white/80">{r?.proveedor_nombre}</td>
                  <td className="py-2 px-3 text-white/70">{r?.producto}</td>
                  <td className="py-2 px-3 text-white/60">{r?.lote || '—'}</td>
                  <td className="py-2 px-3 text-white/70">{r?.temperatura !== null ? `${fmt(r?.temperatura)} °C` : '—'}</td>
                  <td className="py-2 px-3 text-white/60">{r?.estado_embalaje || '—'}</td>
                  <td className="py-2 px-3 text-white/60">{fmtDate(r?.fecha_caducidad)}</td>
                  <td className="py-2 px-3"><StatusBadge status={r?.conforme ? 'CONFORME' : 'INCIDENCIA'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Modal open={showModal} onClose={() => { setShowModal(false); setError(''); }} title="Nueva Recepción de Mercancías">
        <div className="space-y-4">
          <Field label="Proveedor" required>
            <input value={form?.proveedor_nombre} onChange={e => setForm(f => ({ ...f, proveedor_nombre: e?.target?.value }))} className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Producto" required>
            <input value={form?.producto} onChange={e => setForm(f => ({ ...f, producto: e?.target?.value }))} className={inputCls} style={inputStyle} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lote">
              <input value={form?.lote} onChange={e => setForm(f => ({ ...f, lote: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Temperatura (°C)">
              <input type="number" step="0.1" value={form?.temperatura} onChange={e => setForm(f => ({ ...f, temperatura: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Estado embalaje">
              <input value={form?.estado_embalaje} onChange={e => setForm(f => ({ ...f, estado_embalaje: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Fecha caducidad">
              <input type="date" value={form?.fecha_caducidad} onChange={e => setForm(f => ({ ...f, fecha_caducidad: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
          </div>
          <Field label="Conforme">
            <div className="flex items-center gap-3">
              {[true, false]?.map(v => (
                <button key={String(v)} onClick={() => setForm(f => ({ ...f, conforme: v }))}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all"
                  style={{ background: form?.conforme === v ? (v ? `${ACCENT}15` : `${RED}15`) : 'rgba(255,255,255,0.04)', border: `1px solid ${form?.conforme === v ? (v ? `${ACCENT}40` : `${RED}40`) : 'rgba(255,255,255,0.1)'}`, color: form?.conforme === v ? (v ? ACCENT : RED) : 'rgba(255,255,255,0.6)' }}>
                  <Icon name={v ? 'CheckCircle' : 'XCircle'} size={14} />
                  {v ? 'Conforme' : 'No conforme'}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Observaciones">
            <textarea value={form?.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e?.target?.value }))} rows={2} className={inputCls} style={inputStyle} />
          </Field>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={() => { setShowModal(false); setError(''); }} className="flex-1 py-2 rounded-lg text-white/60 text-sm hover:bg-white/5" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// FORMACIÓN TAB
// ════════════════════════════════════════════════════════════════
const FormacionTab = ({ formacion, onRefresh, userProfile }) => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ empleado_nombre: '', formacion: '', fecha: '', fecha_vencimiento: '', observaciones: '' });
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const handleSave = async () => {
    if (!form?.empleado_nombre || !form?.formacion || !form?.fecha) { setError('Empleado, formación y fecha requeridos'); return; }
    setSaving(true); setError('');
    try {
      await supabase?.from('appcc_formacion')?.insert({ ...form, fecha_vencimiento: form?.fecha_vencimiento || null });
      setShowModal(false);
      setForm({ empleado_nombre: '', formacion: '', fecha: '', fecha_vencimiento: '', observaciones: '' });
      onRefresh();
    } catch (e) { setError(e?.message); } finally { setSaving(false); }
  };

  const vencidas = formacion?.filter(f => f?.fecha_vencimiento && f?.fecha_vencimiento <= today) || [];

  return (
    <div className="space-y-5">
      {vencidas?.length > 0 && (
        <div className="p-3 rounded-lg flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
          <Icon name="AlertTriangle" size={16} style={{ color: RED }} />
          <span className="text-red-300 text-sm">{vencidas?.length} formación(es) vencida(s) o próximas a vencer</span>
        </div>
      )}
      <div className="flex justify-end">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
          <Icon name="Plus" size={15} /> Nuevo registro
        </button>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {['Empleado', 'Formación', 'Fecha', 'Vencimiento', 'Estado']?.map(h => (
                  <th key={h} className="text-left text-white/50 font-medium py-2 px-3 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formacion?.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-white/30 py-8">Sin registros de formación</td></tr>
              ) : formacion?.map(f => {
                const vencida = f?.fecha_vencimiento && f?.fecha_vencimiento <= today;
                return (
                  <tr key={f?.id} className="border-b border-white/5 hover:bg-white/3">
                    <td className="py-2 px-3 text-white/80">{f?.empleado_nombre}</td>
                    <td className="py-2 px-3 text-white/70">{f?.formacion}</td>
                    <td className="py-2 px-3 text-white/60">{fmtDate(f?.fecha)}</td>
                    <td className="py-2 px-3" style={{ color: vencida ? RED : 'rgba(255,255,255,0.6)' }}>{fmtDate(f?.fecha_vencimiento)}</td>
                    <td className="py-2 px-3"><StatusBadge status={vencida ? 'INCIDENCIA' : 'CONFORME'} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <Modal open={showModal} onClose={() => { setShowModal(false); setError(''); }} title="Nuevo Registro de Formación">
        <div className="space-y-4">
          <Field label="Empleado" required>
            <input value={form?.empleado_nombre} onChange={e => setForm(f => ({ ...f, empleado_nombre: e?.target?.value }))} className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Formación" required>
            <input value={form?.formacion} onChange={e => setForm(f => ({ ...f, formacion: e?.target?.value }))} placeholder="Ej: Manipulación de alimentos, APPCC básico..." className={inputCls} style={inputStyle} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha" required>
              <input type="date" value={form?.fecha} onChange={e => setForm(f => ({ ...f, fecha: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Vencimiento">
              <input type="date" value={form?.fecha_vencimiento} onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e?.target?.value }))} className={inputCls} style={inputStyle} />
            </Field>
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={() => { setShowModal(false); setError(''); }} className="flex-1 py-2 rounded-lg text-white/60 text-sm hover:bg-white/5" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// INFORME TAB
// ════════════════════════════════════════════════════════════════
const InformeTab = ({ equipos, temperaturas, incidencias, limpiezaRegistros, recepciones, plagas, mantenimiento, formacion }) => {
  const [periodo, setPeriodo] = useState('7');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [generated, setGenerated] = useState(false);

  const getRange = () => {
    const to = new Date();
    if (periodo === 'custom') return { from: customFrom, to: customTo };
    const from = new Date(to);
    from.setDate(from.getDate() - parseInt(periodo));
    return { from: from.toISOString()?.split('T')?.[0], to: to?.toISOString()?.split('T')?.[0] };
  };

  const { from, to } = getRange();

  const filteredTemps = temperaturas?.filter(t => t?.fecha_hora >= from && t?.fecha_hora <= to + 'T23:59:59') || [];
  const filteredInc = incidencias?.filter(i => i?.fecha_hora >= from && i?.fecha_hora <= to + 'T23:59:59') || [];
  const filteredRec = recepciones?.filter(r => r?.fecha >= from && r?.fecha <= to + 'T23:59:59') || [];
  const filteredLimp = limpiezaRegistros?.filter(r => r?.fecha_ejecucion >= from && r?.fecha_ejecucion <= to + 'T23:59:59') || [];

  const equipoStats = equipos?.map(eq => {
    const temps = filteredTemps?.filter(t => t?.equipo_id === eq?.id);
    const fuera = temps?.filter(t => t?.estado === 'FUERA_RANGO');
    const vals = temps?.map(t => t?.temperatura);
    return {
      equipo: eq,
      lecturas: temps?.length,
      fuera: fuera?.length,
      min: vals?.length ? Math.min(...vals) : null,
      max: vals?.length ? Math.max(...vals) : null,
      media: vals?.length ? vals?.reduce((a, b) => a + b, 0) / vals?.length : null,
    };
  });

  return (
    <div className="space-y-5">
      <Card>
        <SectionHeader title="Generar Informe APPCC" subtitle="Selecciona el período para el informe de inspección" />
        <div className="flex flex-wrap items-center gap-3">
          {[['7', '7 días'], ['30', '30 días'], ['90', '3 meses'], ['custom', 'Personalizado']]?.map(([v, l]) => (
            <button key={v} onClick={() => setPeriodo(v)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{ background: periodo === v ? `${GOLD}15` : 'rgba(255,255,255,0.04)', border: `1px solid ${periodo === v ? `${GOLD}40` : 'rgba(255,255,255,0.1)'}`, color: periodo === v ? GOLD : 'rgba(255,255,255,0.6)' }}>
              {l}
            </button>
          ))}
        </div>
        {periodo === 'custom' && (
          <div className="flex gap-3 mt-3">
            <Field label="Desde">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e?.target?.value)} className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Hasta">
              <input type="date" value={customTo} onChange={e => setCustomTo(e?.target?.value)} className={inputCls} style={inputStyle} />
            </Field>
          </div>
        )}
        <button onClick={() => setGenerated(true)}
          className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
          style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}40`, color: GOLD }}>
          <Icon name="FileText" size={15} />
          Generar Informe APPCC
        </button>
      </Card>

      {generated && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl" style={{ background: 'rgba(214,239,78,0.06)', border: '1px solid rgba(214,239,78,0.2)' }}>
            <div className="flex items-center gap-3 mb-2">
              <Icon name="FileCheck" size={18} style={{ color: GOLD }} />
              <span className="text-[#D6EF4E] font-semibold">Informe APPCC — {from} a {to}</span>
            </div>
            <p className="text-white/50 text-xs">Preparado para exportación PDF (próximamente)</p>
          </div>

          {/* Temperaturas */}
          <Card>
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Icon name="Thermometer" size={15} style={{ color: ACCENT }} /> Temperaturas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {equipoStats?.map(s => (
                <div key={s?.equipo?.id} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <p className="text-white/80 font-medium mb-2">{s?.equipo?.nombre}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><p className="text-white/40 text-xs">Mínima</p><p className="text-white font-bold">{s?.min !== null ? `${fmt(s?.min)} °C` : '—'}</p></div>
                    <div><p className="text-white/40 text-xs">Máxima</p><p className="text-white font-bold">{s?.max !== null ? `${fmt(s?.max)} °C` : '—'}</p></div>
                    <div><p className="text-white/40 text-xs">Media</p><p className="text-white font-bold">{s?.media !== null ? `${fmt(s?.media)} °C` : '—'}</p></div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/8 text-xs">
                    <span className="text-white/50">Lecturas: {s?.lecturas}</span>
                    <span style={{ color: s?.fuera > 0 ? RED : ACCENT }}>Fuera de rango: {s?.fuera}</span>
                    <StatusBadge status={s?.fuera > 0 ? 'INCIDENCIA' : s?.lecturas > 0 ? 'CONFORME' : 'PENDIENTE'} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Incidencias */}
          <Card>
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Icon name="AlertTriangle" size={15} style={{ color: AMBER }} /> Incidencias ({filteredInc?.length})</h3>
            {filteredInc?.length === 0 ? <p className="text-white/30 text-sm">Sin incidencias en el período</p> : (
              <div className="space-y-2">
                {filteredInc?.map(i => (
                  <div key={i?.id} className="flex items-start justify-between p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/80 text-sm">{i?.descripcion}</p>
                      {i?.causa && <p className="text-white/50 text-xs mt-0.5">Causa: {i?.causa}</p>}
                      {i?.medida_correctora && <p className="text-white/50 text-xs">Medida: {i?.medida_correctora}</p>}
                    </div>
                    <div className="flex gap-1 ml-2 flex-shrink-0">
                      <StatusBadge status={i?.gravedad} />
                      <StatusBadge status={i?.estado} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Resumen */}
          <Card>
            <h3 className="text-white font-semibold mb-3">Resumen del período</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Total lecturas temp.', value: filteredTemps?.length, color: ACCENT },
                { label: 'Incidencias', value: filteredInc?.length, color: filteredInc?.length > 0 ? RED : ACCENT },
                { label: 'Recepciones', value: filteredRec?.length, color: BLUE },
                { label: 'Tareas limpieza', value: filteredLimp?.length, color: PURPLE },
              ]?.map(item => (
                <div key={item?.label} className="p-3 rounded-lg text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <p className="text-2xl font-bold" style={{ color: item?.color }}>{item?.value}</p>
                  <p className="text-white/50 text-xs mt-1">{item?.label}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════
const APPCCAutocontrol = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Data state
  const [equipos, setEquipos] = useState([]);
  const [temperaturas, setTemperaturas] = useState([]);
  const [incidencias, setIncidencias] = useState([]);
  const [limpiezaPrograma, setLimpiezaPrograma] = useState([]);
  const [limpiezaRegistros, setLimpiezaRegistros] = useState([]);
  const [plagas, setPlagas] = useState([]);
  const [mantenimiento, setMantenimiento] = useState([]);
  const [recepciones, setRecepciones] = useState([]);
  const [formacion, setFormacion] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000)?.toISOString();

      const [
        { data: eq },
        { data: temps, error: tempsErr },
        { data: inc },
        { data: limpProgData },
        { data: limpRegData },
        { data: plagData },
        { data: mantData },
        { data: recData },
        { data: formData },
      ] = await Promise.all([
        supabase?.from('appcc_equipos')?.select('*')?.eq('activo', true)?.order('nombre'),
        supabase?.from('appcc_temperaturas')?.select('*, user_profiles(full_name, email)')?.order('fecha_hora', { ascending: false }),
        supabase?.from('appcc_incidencias')?.select('*')?.order('fecha_hora', { ascending: false }),
        supabase?.from('appcc_limpieza_programa')?.select('*')?.eq('activo', true)?.order('zona'),
        supabase?.from('appcc_limpieza_registros')?.select('*')?.gte('fecha_ejecucion', thirtyDaysAgo)?.order('fecha_ejecucion', { ascending: false }),
        supabase?.from('appcc_plagas')?.select('*')?.order('proxima_revision'),
        supabase?.from('appcc_mantenimiento')?.select('*')?.order('proxima_revision'),
        supabase?.from('appcc_recepciones')?.select('*')?.gte('fecha', thirtyDaysAgo)?.order('fecha', { ascending: false }),
        supabase?.from('appcc_formacion')?.select('*')?.order('fecha_vencimiento'),
      ]);

      if (tempsErr) throw tempsErr;

      setEquipos(eq || []);
      setTemperaturas(temps || []);
      setIncidencias(inc || []);
      setLimpiezaPrograma(limpProgData || []);
      setLimpiezaRegistros(limpRegData || []);
      setPlagas(plagData || []);
      setMantenimiento(mantData || []);
      setRecepciones(recData || []);
      setFormacion(formData || []);
    } catch (e) {
      setError('Error cargando datos APPCC: ' + e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const tabProps = { equipos, temperaturas, incidencias, limpiezaPrograma, limpiezaRegistros, plagas, mantenimiento, recepciones, formacion, onRefresh: loadAll, userProfile };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 40%, #0D0D0D 100%)' }}>
      {/* Header */}
      <div className="px-4 md:px-6 pt-6 pb-4 border-b border-white/8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)' }}>
            <Icon name="ShieldCheck" size={18} style={{ color: ACCENT }} />
          </div>
          <div>
            <h1 className="text-white font-bold text-xl leading-tight">APPCC / Autocontrol</h1>
            <p className="text-white/40 text-sm">Control sanitario · Tienda</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 md:px-6 border-b border-white/8 overflow-x-auto">
        <div className="flex gap-1 py-2 min-w-max">
          {TABS?.map(tab => (
            <button key={tab?.key} onClick={() => setActiveTab(tab?.key)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
              style={{
                background: activeTab === tab?.key ? `${ACCENT}12` : 'transparent',
                border: `1px solid ${activeTab === tab?.key ? `${ACCENT}30` : 'transparent'}`,
                color: activeTab === tab?.key ? ACCENT : 'rgba(255,255,255,0.55)',
              }}>
              <Icon name={tab?.icon} size={13} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-6 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
          </div>
        ) : error ? (
          <div className="p-4 rounded-xl text-red-300 text-sm" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
            {error}
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && <DashboardTab {...tabProps} />}
            {activeTab === 'temperaturas' && <TemperaturasTab {...tabProps} />}
            {activeTab === 'limpieza' && <LimpiezaTab programa={limpiezaPrograma} registros={limpiezaRegistros} onRefresh={loadAll} userProfile={userProfile} />}
            {activeTab === 'incidencias' && <IncidenciasTab {...tabProps} />}
            {activeTab === 'plagas' && <PlagasTab plagas={plagas} onRefresh={loadAll} userProfile={userProfile} />}
            {activeTab === 'mantenimiento' && <MantenimientoTab mantenimiento={mantenimiento} equipos={equipos} onRefresh={loadAll} userProfile={userProfile} />}
            {activeTab === 'recepciones' && <RecepcionesTab recepciones={recepciones} onRefresh={loadAll} userProfile={userProfile} />}
            {activeTab === 'formacion' && <FormacionTab formacion={formacion} onRefresh={loadAll} userProfile={userProfile} />}
            {activeTab === 'informe' && <InformeTab equipos={equipos} temperaturas={temperaturas} incidencias={incidencias} limpiezaRegistros={limpiezaRegistros} recepciones={recepciones} plagas={plagas} mantenimiento={mantenimiento} formacion={formacion} />}
          </>
        )}
      </div>
    </div>
  );
};

export default APPCCAutocontrol;
